import React from 'react'

import cn from 'classnames'

type PaginationProps = {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
	const renderPageNumbers = () => {
		type PageItem = number | 'ellipsis'
		const pageNumbers: PageItem[] = []
		const maxVisiblePages = 4 // Number of pages to show before ellipsis

		if (totalPages <= maxVisiblePages) {
			// If total pages is less than max visible, show all pages
			for (let i = 0; i < totalPages; i++) {
				pageNumbers.push(i)
			}
		} else {
			if (currentPage < 2) {
				// First two pages
				pageNumbers.push(0, 1)
			} else {
				// Show current page and one before it
				pageNumbers.push(currentPage - 1, currentPage)
			}

			// Add ellipsis if not near the end
			if (currentPage < totalPages - 3) {
				pageNumbers.push('ellipsis')
			}

			// Always show last two pages
			if (!pageNumbers.includes(totalPages - 2)) {
				pageNumbers.push(totalPages - 2)
			}
			if (!pageNumbers.includes(totalPages - 1)) {
				pageNumbers.push(totalPages - 1)
			}
		}

		return pageNumbers.map((pageNum, index) => {
			if (pageNum === 'ellipsis') {
				return (
					<span key={`ellipsis-${index}`} className='px-2 text-gray-400'>
						...
					</span>
				)
			}

			return (
				<button aria-label={`Page ${pageNum + 1}`}
					key={`page-${pageNum}`}
					onClick={() => onPageChange(pageNum)}
					className={cn(
						'flex h-8 w-8 items-center justify-center rounded text-sm font-medium transition-colors',
						currentPage === pageNum ? 'bg-blue-500 text-white hover:bg-blue-600' : 'text-gray-400 hover:bg-gray-700/50'
					)}
				>
					{pageNum + 1}
				</button>
			)
		})
	}

	return (
		<div className='flex items-center justify-end border-t border-gray-700/50 px-4 py-3'>
			<div className='flex items-center space-x-4'>
				<span className='text-sm text-gray-400'>
					Page {currentPage + 1} of {totalPages}
				</span>
				<div className='flex items-center space-x-1'>
					<button
						onClick={() => onPageChange(currentPage - 1)}
						disabled={currentPage === 0}
						className={cn(
							'flex h-8 w-8 items-center justify-center rounded text-sm transition-colors',
							currentPage === 0 ? 'cursor-not-allowed text-gray-600' : 'text-gray-400 hover:bg-gray-700/50'
						)}
					>
						<svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
						</svg>
					</button>
					{renderPageNumbers()}
					<button
						onClick={() => onPageChange(currentPage + 1)}
						disabled={currentPage >= totalPages - 1}
						className={cn(
							'flex h-8 w-8 items-center justify-center rounded text-sm transition-colors',
							currentPage >= totalPages - 1 ? 'cursor-not-allowed text-gray-600' : 'text-gray-400 hover:bg-gray-700/50'
						)}
					>
						<svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
						</svg>
					</button>
				</div>
			</div>
		</div>
	)
}

export default Pagination
